import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FileImage, Upload, Loader2, Check, AlertTriangle, X, Camera } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface ExtractedInvoice {
  vendor: string;
  invoiceNo: string;
  date: string;
  amount: number;
}

interface InvoiceUploadProps {
  onSuccess?: (invoice: ExtractedInvoice) => void;
}

export function InvoiceUpload({ onSuccess }: InvoiceUploadProps) {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [extracted, setExtracted] = useState<ExtractedInvoice | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setFile(selectedFile);
    setExtracted(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(selectedFile);
  };

  const analyzeInvoice = async () => {
    if (!file || !user || !preview) return;

    setAnalyzing(true);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-invoice', {
        body: { imageData: preview },
      });

      if (error) throw error;

      if (data.error && data.vendor === 'Analysis Failed') {
        throw new Error(data.error);
      }

      setExtracted(data);
      toast.success('Invoice analyzed successfully');
    } catch (error: any) {
      console.error('Analysis error:', error);
      if (error.message?.includes('Rate limit')) {
        toast.error('Rate limit exceeded. Please wait a moment and try again.');
      } else if (error.message?.includes('Payment required')) {
        toast.error('Please add credits to continue analyzing invoices.');
      } else {
        toast.error(error.message || 'Failed to analyze invoice');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const saveInvoice = async () => {
    if (!extracted || !user) return;

    setSaving(true);
    try {
      const { error } = await supabase.from('invoices').insert({
        user_id: user.id,
        vendor: extracted.vendor,
        invoice_no: extracted.invoiceNo,
        date: extracted.date,
        amount: extracted.amount,
        status: 'pending',
        file_name: file?.name || 'uploaded_invoice',
      });

      if (error) throw error;

      await supabase.from('logs').insert({
        user_id: user.id,
        msg: `Invoice added: ${extracted.vendor} - ₹${extracted.amount.toLocaleString()}`,
        level: 'success',
        time: new Date().toISOString(),
      });

      toast.success('Invoice saved successfully');
      onSuccess?.(extracted);
      resetForm();
    } catch (error: any) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview(null);
    setExtracted(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  return (
    <Card className="p-6 rounded-3xl border-border/50 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-2xl bg-gradient-primary shadow-glow">
            <FileImage className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-foreground">Invoice Scanner</h3>
            <p className="text-sm text-muted-foreground">Upload or capture invoice to extract details</p>
          </div>
        </div>
        {(file || extracted) && (
          <Button variant="ghost" size="icon" onClick={resetForm} className="rounded-2xl">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!preview ? (
          <motion.div
            key="upload"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-2 gap-4"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileSelect}
            />
            
            <Button
              variant="outline"
              className="h-32 rounded-3xl flex-col gap-3 border-dashed hover:bg-surface"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-8 w-8 text-primary" />
              <span className="text-sm">Upload Image</span>
            </Button>

            <Button
              variant="outline"
              className="h-32 rounded-3xl flex-col gap-3 border-dashed hover:bg-surface"
              onClick={() => cameraInputRef.current?.click()}
            >
              <Camera className="h-8 w-8 text-primary" />
              <span className="text-sm">Take Photo</span>
            </Button>
          </motion.div>
        ) : (
          <motion.div
            key="preview"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="space-y-4"
          >
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-surface">
              <img 
                src={preview} 
                alt="Invoice preview" 
                className="w-full h-full object-contain"
              />
              {analyzing && (
                <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center">
                  <div className="text-center space-y-3">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                    <p className="text-sm text-muted-foreground">Analyzing invoice...</p>
                  </div>
                </div>
              )}
            </div>

            {!extracted && !analyzing && (
              <Button 
                onClick={analyzeInvoice} 
                className="w-full rounded-2xl h-12"
                disabled={analyzing}
              >
                <FileImage className="h-4 w-4 mr-2" />
                Analyze Invoice
              </Button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Extracted Data Display */}
      <AnimatePresence>
        {extracted && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            <div className="p-5 rounded-2xl bg-success/10 border border-success/20">
              <div className="flex items-center gap-2 mb-4">
                <Check className="h-5 w-5 text-success" />
                <span className="font-medium text-success">Invoice Extracted</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground uppercase">Vendor</div>
                  <div className="font-medium text-foreground">{extracted.vendor}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase">Invoice No</div>
                  <div className="font-medium text-foreground">{extracted.invoiceNo}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase">Date</div>
                  <div className="font-medium text-foreground">{extracted.date}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase">Amount</div>
                  <div className="font-bold text-primary text-lg">
                    ₹{extracted.amount.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={resetForm}
                className="flex-1 rounded-2xl"
              >
                Upload Another
              </Button>
              <Button 
                onClick={saveInvoice}
                disabled={saving}
                className="flex-1 rounded-2xl"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Save Invoice
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
